
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Spin } from "antd";
import { CheckCircleFilled, TrophyFilled, SmileFilled } from "@ant-design/icons";
import api from '../services/api';

const SubscriptionSuccess: React.FC = () => {
    const [searchParams] = useSearchParams();
    // The API-plan checkout flow appends ?kind=api on its success_url
    // so this page can render an integration-flavored success screen.
    // The default B2C-themed copy and the GET /subscriptions/me/
    // feature list would both be wrong for a Connect / Connect Pro
    // customer: their sub is not the user's "active" B2C subscription
    // and that endpoint is scoped to B2C plans only.
    const isApiPlan = (searchParams.get('kind') || '').toLowerCase() === 'api';

    const [features, setFeatures] = useState<string[]>([]);
    const [planName, setPlanName] = useState<string | null>(null);
    const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
    const [loading, setLoading] = useState(!isApiPlan);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isApiPlan) {
            // No need to hit the B2C subscription endpoint. The
            // welcome email + API Key Settings page are authoritative
            // for the API plan; this page just confirms success.
            return;
        }
        setLoading(true);
        setError(null);
        // Fetch the user's active subscription plan
        api.get('subscriptions/me/')
            .then(res => {
                // Try to get features from the plan object
                let plan = res.data?.plan;
                let planFeatures: string[] = [];
                if (plan && Array.isArray(plan.features)) {
                    planFeatures = plan.features;
                } else if (plan && plan.features && typeof plan.features === 'object') {
                    // If features is a boolean map, show enabled features
                    planFeatures = Object.entries(plan.features)
                        .filter(([_, val]) => val)
                        .map(([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
                }
                setFeatures(planFeatures);
                setPlanName(plan?.name || null);
                // Set next billing date if available
                if (res.data && res.data.next_billing_date) {
                    setNextBillingDate(res.data.next_billing_date);
                } else {
                    setNextBillingDate(null);
                }
                setLoading(false);
            })
            .catch(() => {
                setError('Could not load your subscription details.');
                setLoading(false);
            });
    }, [isApiPlan]);

    if (isApiPlan) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    background: "linear-gradient(135deg, #f8faff 0%, #e3f0ff 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    padding: 0,
                }}
            >
                <div
                    style={{
                        background: "#fff",
                        borderRadius: 32,
                        boxShadow: "0 4px 32px #e5eaf2",
                        padding: "48px 48px 40px 48px",
                        maxWidth: 600,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        position: "relative",
                    }}
                >
                    <div style={{ position: "absolute", top: -44, left: "50%", transform: "translateX(-50%)" }}>
                        <TrophyFilled style={{ fontSize: 64, color: "#FFD600", filter: "drop-shadow(0 2px 8px #ffe082)" }} />
                    </div>
                    <CheckCircleFilled style={{ fontSize: 56, color: "#43a047", marginBottom: 12 }} />
                    <h1 style={{
                        fontWeight: 900,
                        fontSize: 26,
                        color: "#23244A",
                        marginBottom: 8,
                        marginTop: 8,
                        textAlign: "center",
                        letterSpacing: 0.2,
                    }}>
                        API subscription activated!
                    </h1>
                    <div style={{
                        fontSize: 16,
                        color: "#23244A",
                        opacity: 0.85,
                        fontWeight: 500,
                        marginBottom: 20,
                        textAlign: "center",
                        lineHeight: 1.6,
                    }}>
                        Welcome to <span style={{ color: "#0051FF", fontWeight: 700 }}>TestInvest</span>.
                        Your integration plan is now active.
                    </div>
                    <div style={{
                        background: '#E6F0FF',
                        borderRadius: 12,
                        padding: '16px 20px',
                        marginBottom: 28,
                        color: '#23244A',
                        fontSize: 14,
                        lineHeight: 1.6,
                        textAlign: 'left',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}>
                        <strong>What's next:</strong>
                        <ol style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                            <li>Open <strong>API Key Settings</strong> below to <strong>reveal your API key</strong>. It's shown once, then never visible again — copy it to a secrets manager straight away.</li>
                            <li>While you're there, configure your webhook URL, IP allowlist, and rate limits.</li>
                            <li>Read the <a href="/docs/api-integration">integration guide</a> for endpoint, payload, and webhook details.</li>
                            <li>Send your first session create request when you're ready.</li>
                        </ol>
                    </div>
                    <div style={{ display: "flex", gap: 16, width: "100%", justifyContent: "center" }}>
                        <Link to="/api-key-settings">
                            <Button type="primary" size="large" style={{ borderRadius: 24, fontWeight: 700, minWidth: 200 }}>
                                Open API Key Settings
                            </Button>
                        </Link>
                        <Link to="/dashboard">
                            <Button size="large" style={{ borderRadius: 24, fontWeight: 700, minWidth: 140 }}>
                                Dashboard
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #f8faff 0%, #e3f0ff 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                padding: 0,
            }}
        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: 32,
                    boxShadow: "0 4px 32px #e5eaf2",
                    padding: "48px 48px 40px 48px",
                    maxWidth: 600,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                }}
            >
                <div style={{ position: "absolute", top: -44, left: "50%", transform: "translateX(-50%)" }}>
                    <TrophyFilled style={{ fontSize: 64, color: "#FFD600", filter: "drop-shadow(0 2px 8px #ffe082)" }} />
                </div>
                <CheckCircleFilled style={{ fontSize: 56, color: "#43a047", marginBottom: 12 }} />
                <h1 style={{
                    fontWeight: 900,
                    fontSize: 26,
                    color: "#23244A",
                    marginBottom: 8,
                    marginTop: 8,
                    textAlign: "center",
                    letterSpacing: 0.2,
                }}>
                    {planName ? `${planName} Subscription Activated!` : 'Subscription Activated!'}
                </h1>
                <div style={{
                    fontSize: 18,
                    color: "#23244A",
                    opacity: 0.85,
                    fontWeight: 500,
                    marginBottom: 18,
                    textAlign: "center",
                }}>
                    Thank you for joining <span style={{ color: "#0051FF", fontWeight: 700 }}>Test Invest</span>.<br />
                    Your access is now <span style={{ color: "#43a047", fontWeight: 700 }}>active</span> and ready to access all the amazing features:<br />
                </div>
                {loading ? (
                    <div style={{ minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <Spin size="large" tip="Loading your plan features..." />
                    </div>
                ) : error ? (
                    <div style={{ color: '#dd0000ff', fontWeight: 600, marginBottom: 18 }}>{error}</div>
                ) : (
                    <>
                        <div
                            style={{
                                color: "#23244A",
                                fontSize: 15,
                                marginBottom: 18,
                                width: '100%',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0,
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                        >
                            {features.length > 0 ? (
                                <>
                                    <ul style={{
                                        flex: '1 1 50%',
                                        minWidth: 180,
                                        margin: 0,
                                        paddingLeft: 24,
                                        listStyle: 'disc',
                                        textAlign: 'left',
                                    }}>
                                        {features.filter((_, i) => i % 2 === 0).map((feature, idx) => (
                                            <li key={"col1-" + idx}>{feature}</li>
                                        ))}
                                    </ul>
                                    <ul style={{
                                        flex: '1 1 50%',
                                        minWidth: 180,
                                        margin: 0,
                                        paddingLeft: 24,
                                        listStyle: 'disc',
                                        textAlign: 'left',
                                    }}>
                                        {features.filter((_, i) => i % 2 === 1).map((feature, idx) => (
                                            <li key={"col2-" + idx}>{feature}</li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <ul style={{ flex: '1 1 100%', margin: 0, paddingLeft: 24, listStyle: 'disc', textAlign: 'left' }}>
                                    <li>All premium features included</li>
                                </ul>
                            )}
                        </div>
                        {nextBillingDate && (
                            <div style={{
                                color: '#23244A',
                                fontSize: 15,
                                marginBottom: 28,
                                background: '#E6F0FF',
                                borderRadius: 12,
                                padding: '12px 18px',
                                fontWeight: 600,
                                textAlign: 'center',
                                boxShadow: '0 2px 8px #e5eaf2',
                            }}>
                                <span style={{ color: '#0051FF', fontWeight: 700 }}>
                                    Next renewal:&nbsp;
                                    {new Date(nextBillingDate).toLocaleDateString(undefined, {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })}
                                </span>
                                <br />
                                <span style={{ fontWeight: 400, color: '#23244A', fontSize: 13, opacity: 0.85 }}>
                                    Stripe subscriptions renew on the same calendar day each {planName && planName.toLowerCase().includes('year') ? 'year' : 'month'}, based on your original purchase date. If a month has fewer days, renewal occurs on the closest valid day.
                                </span>
                            </div>
                        )}
                    </>
                )}
                <div style={{ display: "flex", gap: 16, width: "100%", justifyContent: "center" }}>
                    <Link to="/dashboard">
                        <Button type="primary" size="large" style={{ borderRadius: 24, fontWeight: 700, minWidth: 160 }}>
                            Go to Dashboard
                        </Button>
                    </Link>
                    <Link to="/subscriptions">
                        <Button size="large" style={{ borderRadius: 24, fontWeight: 700, minWidth: 140 }}>
                            View Plans
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionSuccess;
